import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, onSnapshot, getDocs, serverTimestamp, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { writeUserDocument, deleteUserDocument, uploadImageToStorage, moveUserDocument, updateProfileUsage } from '@services/firebaseUser';
import { readGlobalProfiles, writeGlobalProfile as writeGlobalDocument, fetchCombinations, readFlatTemplate } from '@services/firebaseGlobal';
import { getFirebase } from '@services/firebase';
import CustomLargeCardUser from '@components/CustomLargeCardUser';
import CustomLargeCardSuper from '@components/CustomLargeCardSuper';
import CustomProfileCard from '@components/CustomProfileCard';
import { FaPlus, FaSlidersH, FaEdit, FaBirthdayCake, FaFileImport, FaUserCog, FaTimes, FaCheckSquare, FaTrash } from 'react-icons/fa';
import SocialProvider from '@components/social/SocialProvider';
import SocialShareButton from '@components/social/SocialShareButton';
import ShareSheet from '@components/social/ShareSheet';
import ImportModal from '@components/social/ImportModal';
import '../index.css';
import { useProfileActions } from '../hooks/useProfileActions';
import { searchByImage } from '../services/imageSearch';
import { multiKeywordMatch } from '../utils/searchUtils';
import ProcessGuidePanel from '@components/ProcessGuidePanel';
import { useToast } from '../contexts/ToastContext';
import { useAlert, useConfirm } from '../contexts/ToastContext';

const CUSTOM_BUYER_STEPS = [
  { label: 'Edit Added Profile',  sub: 'Customise your requirement',   icon: <FaEdit />,         action: true, info: { image: '/custom-qty-help.png', darkImage: '/custom-qty-help-dark.png', notes: [
    'Up to three images can be attached to the design created (\'+\' button) in Custom page.',
    'To design added from Home or Favourite, setting the quantity allows Flavour alteration.',
    'Enabled for design created (\'+\' button) in Custom page. 2 Tier ≥1.5 kg / 3 Tier ≥2.5 kg',
  ] } },
  { label: 'Create New Profile',  sub: 'Build a design from scratch',  icon: <FaPlus />,         or: true     },
  { label: 'Add to Enquiry',      sub: 'Start enquiry and order',      icon: <FaBirthdayCake />, final: true  },
];

const { auth, db } = getFirebase();

// Newest first. A profile saved a moment ago has `created` = null until the server stamps it,
// so it counts as the newest; profiles from before `created` existed go last.
const millis = (v) => {
  if (v == null) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v === 'number') return v;
  return Date.parse(v) || 0;
};
const createdAtOf = (p) => (p.created === null ? Number.MAX_SAFE_INTEGER : millis(p.created) || millis(p.addedAt) || millis(p.updated));

// The words a cake is found by: what Home searches, plus each tier's flavour and filling.
const searchFieldsOf = (p) => [
  p.theme || '', p.cflavour || '', p.cfilling || p.cfilling1 || '', p.sponge || '', p.cover || '',
  ...(Array.isArray(p.cevent) ? p.cevent : []),
  ...(Array.isArray(p.cshape) ? p.cshape : []),
  ...(Array.isArray(p.tiers) ? p.tiers.flatMap(t => [t?.cflavour || '', t?.cfilling || '', t?.sponge || '']) : []),
];

/** Selection overlay on a profile card: the whole card toggles, the box shows the state. */
const SelectBox = ({ checked, onToggle }) => (
  <div
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    role="checkbox"
    aria-checked={checked}
    style={{
      position: 'absolute', inset: 0, zIndex: 20, cursor: 'pointer', borderRadius: 8,
      background: checked ? 'rgba(250,72,72,0.18)' : 'rgba(0,0,0,0.04)',
      outline: checked ? '2px solid #fa4848' : 'none',
    }}
  >
    <span style={{
      position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: 5,
      border: '2px solid #fa4848', background: checked ? '#fa4848' : '#fff', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700,
    }}>{checked ? '✓' : ''}</span>
  </div>
);

const Custom = ({ searchQuery = '' }) => {
  const [user] = useAuthState(auth);
  const showToast = useToast();
  const showAlert = useAlert();
  const confirm = useConfirm();
  const { userRole, customProfiles } = useProfileActions();
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showOngoingModal, setShowOngoingModal] = useState(false);
  const [availableFlavours, setAvailableFlavours] = useState([]);
  const [availableFillings, setAvailableFillings] = useState([]);
  const [availableSponges, setAvailableSponges] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [coverCombinations, setCoverCombinations] = useState([]);
  const [cEvents, setCEvents] = useState([]);
  const [cShapes, setCShapes] = useState([]);
  const [cakeChargeData, setCakeChargeData] = useState({});
  const [personServedData, setPersonServedData] = useState({});
  // Social (Super): the profile just created (offered for one-click sharing), import + accounts dialogs
  const [justCreated, setJustCreated] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showAccounts, setShowAccounts] = useState(false);
  const [error, setError] = useState(null);
  // Super: pick profiles to delete several at once
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Ticked: the selected cakes are deleted because the import went wrong (bad photo, wrong
  // details) — the next import brings them in again. Unticked: not wanted, never re-imported.
  const [importIssue, setImportIssue] = useState(false);
  // Super: search by words (top bar) or by photo (camera in the top bar), as on Home.
  const [imageSearch, setImageSearch] = useState(null);
  const imageSearchRun = useRef(0);
  const profilesRef = useRef([]);

  // Re-open CustomLargeCardSuper if returning from ImageStudio with a modified image
  useEffect(() => {
    const returnedImage = sessionStorage.getItem('studio_returned_image');
    const returnedProfileId = sessionStorage.getItem('studio_return_profile_id');
    if (!returnedImage || !customProfiles) return;
    const targetProfile = returnedProfileId
      ? (customProfiles || []).find(p => p.id === returnedProfileId)
      : null;
    if (returnedProfileId && !targetProfile) return; // profiles not loaded yet
    // Do NOT clear sessionStorage here — CustomLargeCardSuper will read it on mount
    // to inject the returned image into formData (while keeping initialFormData = original)
    openProfileCard(targetProfile || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customProfiles]);

  // Fetch cakeChargeData on mount so profile cards always have it available
  useEffect(() => {
    const fetchChargeData = async () => {
      try {
        const chargeItems = await fetchCombinations('cCakeCharge');
        const chargeMap = {};
        const psMap = {};
        chargeItems.forEach(({ variant, cost, personServed }) => {
          if (variant) {
            chargeMap[variant] = cost;
            if (personServed) psMap[variant] = personServed;
          }
        });
        setCakeChargeData(chargeMap);
        setPersonServedData(psMap);
      } catch (err) {
        console.error('Error fetching cake charge data:', err.message);
      }
    };
    if (user) fetchChargeData();
  }, [user]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedCombinations = await fetchCombinations('oCombination');
        setCombinations(fetchedCombinations);

        const fetchedCoverCombinations = await fetchCombinations('oCoverCombi');
        setCoverCombinations(fetchedCoverCombinations);

        const fetchedCEvents = await readFlatTemplate('cEvents');
        setCEvents(fetchedCEvents);

        const fetchedCShapes = await readFlatTemplate('cShape');
        setCShapes(fetchedCShapes);

        // Process combinations to extract flavours, fillings, and sponges
        const flavours = [...new Set(fetchedCombinations.map(combo => combo.flavour).filter(Boolean))];
        const fillings = [...new Set(fetchedCombinations.map(combo => combo.filling).filter(Boolean))];
        const sponges = [...new Set(fetchedCombinations.flatMap(combo => combo.sponge).filter(Boolean))];

        setAvailableFlavours(flavours);
        setAvailableFillings(fillings);
        setAvailableSponges(sponges);

        if (flavours.length === 0) console.warn('No flavours found.');
        if (fillings.length === 0) console.warn('No fillings found.');
        if (sponges.length === 0) console.warn('No sponges found.');
        if (fetchedCoverCombinations.length === 0) console.warn('No cover combinations found in oCoverCombi.');
      } catch (err) {
        console.error('Error fetching available fields:', err.message, err.stack);
        setError('We couldn’t load the options for creating a profile. Please try again later.');
      }
    };

    if (showProfileCard) {
      // Fetch data when the profile card opens
      fetchData();
    } else {
      // Clear data when the profile card closes
      setAvailableFlavours([]);
      setAvailableFillings([]);
      setAvailableSponges([]);
      setCombinations([]);
      setCoverCombinations([]);
      setCEvents([]);
      setCShapes([]);
      // keep cakeChargeData and personServedData — fetched on mount, not per-card-open
    }
  }, [showProfileCard]);

  const handleAddProfile = async (formData) => {
    if (!user || userRole === 'Admin') return;
    try {
      if (userRole === 'Super') {
        let imageUrl = '';
        if (formData.cimage && formData.cimage.startsWith('data:')) {
          const response = await fetch(formData.cimage);
          const blob = await response.blob();
          imageUrl = await uploadImageToStorage(user.uid, blob);
        } else {
          imageUrl = formData.cimage;
        }

        const docRef = doc(collection(db, 'custom'));
        const profileData = {
          theme: formData.theme,
          cflavour: formData.cflavour,
          cover: formData.cover,
          cfilling: formData.cfilling,
          frosting: formData.frosting,
          sponge: formData.sponge,
          ccost: stripEuro(formData.ccost) || null,
          costest: stripEuro(formData.costest) || null,
          cimage: imageUrl,
          cevent: formData.cevent || [],
          cshape: formData.cshape || [],
          isFromHome: false,
        };

        await writeUserDocument('custom', user.uid, profileData, docRef.id);
        //showAlert('Profile added successfully!');
        setShowProfileCard(false);
        setSelectedProfile(null);
        setJustCreated({ ...profileData, id: docRef.id, userId: user.uid });
      } else {
        let imageUrls = [];
        if (formData.images && formData.images.length > 0) {
          for (const image of formData.images) {
            if (image && image.startsWith('data:')) {
              const response = await fetch(image);
              const blob = await response.blob();
              const imageUrl = await uploadImageToStorage(user.uid, blob);
              imageUrls.push(imageUrl);
            } else {
              imageUrls.push(image);
            }
          }
        }

        const profileData = {
          cover: formData.cover || null,
          frosting: formData.frosting || null,
          eggless: formData.eggless || false,
          glutenFree: formData.glutenFree || false,
          quantity: formData.quantity || null,
          ccost: stripEuro(formData.ccost) || null,
          costest: stripEuro(formData.costest) || null,
          images: imageUrls,
          tiers: formData.tiers || [],
          tierCount: formData.tierCount || 1,
          multiTier: formData.multiTier || false,
          moreTier: formData.moreTier || false,
          isFromHome: false,
          cshape: formData.cshape || [],
        };

        if (formData.theme) {
          profileData.theme = formData.theme;
        }

        await writeUserDocument('custom', user.uid, profileData);
        //showAlert('Profile added successfully!');
        setShowProfileCard(false);
        setSelectedProfile(null);
      }
    } catch (err) {
      console.error('Error adding custom profile:', err.message, err.stack);
      showAlert(`Failed to add custom profile: ${err.message}`);
    }
  };

  const handleUpdateProfile = async (formData) => {
    if (!user || !selectedProfile) return;
    try {
      if (userRole === 'Super') {
        let imageUrl = selectedProfile.cimage || '';
        if (formData.cimage && formData.cimage.startsWith('data:')) {
          const response = await fetch(formData.cimage);
          const blob = await response.blob();
          imageUrl = await uploadImageToStorage(user.uid, blob);
        } else {
          imageUrl = formData.cimage;
        }

        const updatedData = {
          globalId: selectedProfile.globalId || null, 
          theme: formData.theme,
          cflavour: formData.cflavour,
          cover: formData.cover,
          cfilling: formData.cfilling,
          frosting: formData.frosting,
          sponge: formData.sponge,
          ccost: stripEuro(formData.ccost),
          costest: stripEuro(formData.costest) || null,
          cimage: imageUrl,
          cevent: formData.cevent || [],
          cshape: formData.cshape || [],
          isFromHome: false,
          // Saved by the Super after an import → the defaulted fields have been looked at.
          needsReview: false,
          defaultedFields: [],
        };
        await writeUserDocument('custom', user.uid, updatedData, selectedProfile.id);
        //showAlert('Profile updated successfully!');
        setShowProfileCard(false);
        setSelectedProfile(null);
      } else {
        let imageUrls = selectedProfile.images || [];
        if (!selectedProfile.isFromHome && formData.images && formData.images.length > 0) {
          imageUrls = [];
          for (const image of formData.images) {
            if (image && image.startsWith('data:')) {
              const response = await fetch(image);
              const blob = await response.blob();
              const imageUrl = await uploadImageToStorage(user.uid, blob);
              imageUrls.push(imageUrl);
            } else {
              imageUrls.push(image);
            }
          }
        }

        const updatedData = {
          cover: formData.cover || null,
          frosting: formData.frosting || null,
          eggless: formData.eggless || false,
          glutenFree: formData.glutenFree || false,
          quantity: formData.quantity || null,
          ccost: stripEuro(formData.ccost) || null,
          costest: stripEuro(formData.costest) || null,
          images: imageUrls,
          tiers: formData.tiers || [],
          tierCount: formData.tierCount || 1,
          multiTier: formData.multiTier || false,
          moreTier: formData.moreTier || false,
          isFromHome: false,
          cshape: selectedProfile.cshape || [],
          globalId: selectedProfile.globalId || null,
        };

        if (formData.theme) {
          updatedData.theme = formData.theme;
        }

        await writeUserDocument('custom', user.uid, updatedData, selectedProfile.id);
        //showAlert('Profile updated successfully!');
        setShowProfileCard(false);
        setSelectedProfile(null);
      }
    } catch (err) {
      console.error('Error updating custom profile:', err.message, err.stack);
      showAlert(`Failed to update custom profile: ${err.message}`);
    }
  };

  // An imported cake that is deleted is remembered with WHY: 'deleted' (not wanted — the next
  // import skips it) or 'reimport' (deleted as an import issue — the next import brings it back).
  // Publishing to Home removes the Custom copy its own way and is not recorded here.
  const rememberDeletedImport = async (profile, kind = 'deleted') => {
    const from = profile?.importedFrom;
    if (!from?.platform || !from?.postId || !user) return;
    const id = `${user.uid}_${from.platform}_${String(from.postId).replace(/[^A-Za-z0-9_-]/g, '_')}`;
    await setDoc(doc(db, 'socialImportSkip', id), {
      uid: user.uid, platform: from.platform, postId: String(from.postId), postUrl: from.postUrl || null, kind,
      theme: profile.theme || null, image: profile.cimage || null, deletedAt: serverTimestamp(),
    }).catch((e) => console.warn('Could not remember the deleted import:', e.message));
  };

  const handleDelete = async (docId) => {
    try {
      const profile = (customProfiles || []).find(p => p.id === docId);
      if (profile) await rememberDeletedImport(profile);
      await deleteUserDocument('custom', user.uid, docId);
      //showAlert('Profile deleted successfully!');
    } catch (err) {
      console.error('Error deleting custom profile:', err.message, err.stack);
      showAlert(`Failed to delete custom profile: ${err.message}`);
    }
  };

  const toggleSelected = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const leaveSelect = () => { setSelectMode(false); setSelected(new Set()); setImportIssue(false); };
  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const why = importIssue ? ' They are marked as an import issue, so the next import brings them in again.' : '';
    if (!(await confirm(`Delete ${ids.length} profile${ids.length === 1 ? '' : 's'}? This cannot be undone.${why}`))) return;
    setBulkBusy(true);
    let failed = 0;
    for (const id of ids) {
      try {
        const profile = (customProfiles || []).find(p => p.id === id);
        if (profile) await rememberDeletedImport(profile, importIssue ? 'reimport' : 'deleted');
        await deleteUserDocument('custom', user.uid, id);
      } catch (err) {
        failed++;
        console.error('Bulk delete failed for', id, err.message);
      }
    }
    setBulkBusy(false);
    leaveSelect();
    showToast(failed ? `${ids.length - failed} deleted, ${failed} failed` : `${ids.length} deleted`);
  };

  const stripEuro = (v) => v != null ? String(v).replace(/[^0-9.]/g, '') || null : null;

  const handlePublish = async (profile) => {
    try {
      const newTemplate = {
        cflavour: profile.cflavour || profile.tiers?.[0]?.cflavour || null,
        cfilling: profile.cfilling || profile.tiers?.[0]?.cfilling || profile.cfilling1 || null,
        cover: profile.cover || null,
        frosting: profile.frosting || null,
        sponge: profile.sponge || profile.tiers?.[0]?.sponge || null,
        ccost: stripEuro(profile.ccost || profile.costest) || null,
        cimage: profile.cimage || (profile.images && profile.images[0]) || null,
        images: profile.images || [],
        id: profile.id,
        publishedBy: user.uid,
        publishedAt: serverTimestamp(),
        isflag: false,
        cevent: profile.cevent || [],
        cshape: profile.cshape || [],
      };
      if (profile.theme) {
        newTemplate.theme = profile.theme;
      }
      await writeGlobalDocument('ctemplatespublished', profile.id, newTemplate);
      // Delete only the Firestore doc — do NOT delete the storage image since it is
      // now referenced by the published home template.
      const { db: firestoreDb } = getFirebase();
      await deleteDoc(doc(firestoreDb, 'custom', profile.id));
      // Clear inCustom usage flag for the original home template (if this was added from home)
      if (profile.globalId) {
        await updateProfileUsage(profile.globalId, user.uid, 'custom', false).catch((err) =>
          console.warn('updateProfileUsage cleanup after publish failed:', err.message)
        );
      }
      //showAlert('Profile published to home successfully!');
    } catch (err) {
      console.error('Error moving profile to global templates:', err.message, err.stack);
      showAlert(`Failed to publish profile: ${err.message}`);
    }
  };

  const handleAddToCart = async (profile) => {
    try {
      const BLOCKING_STATES = ['Request Review', 'Review Pending', 'Review In-Progress', 'Review Feedback'];
      const enqSnap = await getDocs(query(collection(db, 'enquiry'), where('userId', '==', user.uid)));
      const hasOngoing = enqSnap.docs.some(d => { const s = d.data().reqState; return s && BLOCKING_STATES.includes(s); });
      if (hasOngoing) { setShowOngoingModal(true); return; }
      await moveUserDocument('custom', 'enquiry', user.uid, profile.id);
      showToast('Added to enquiry!');
    } catch (err) {
      console.error('Error moving profile to enquiry:', err.message, err.stack);
      showToast(`Failed to add profile to cart: ${err.message}`);
    }
  };

  useEffect(() => { profilesRef.current = customProfiles || []; }, [customProfiles]);

  // Photo search (Super): the camera in the top bar sends the photo; matches are ranked against
  // this person's own cakes and their image index.
  useEffect(() => {
    if (userRole !== 'Super' || !user) return undefined;
    const onImageSearch = async (e) => {
      const file = e.detail?.file;
      const run = ++imageSearchRun.current;
      if (!file) { setImageSearch(null); return; }
      setImageSearch({ status: 'working', message: 'Reading your photo…', ranking: null, tags: [] });
      try {
        const res = await searchByImage(file, profilesRef.current, (message) => {
          if (run === imageSearchRun.current) setImageSearch(st => (st ? { ...st, message } : st));
        }, { source: 'custom', uid: user.uid });
        if (run !== imageSearchRun.current) return;
        setImageSearch({
          status: 'done',
          message: res.mode === 'exact'
            ? `${res.exactCount === 1 ? 'This cake is already in Custom' : `${res.exactCount} matching cakes in Custom`}${res.similarCount ? ' — similar cakes below' : ''}`
            : res.isCake === false
              ? 'That photo does not look like a cake'
              : res.results.length
                ? 'Exact match not found, but these cakes look similar'
                : 'Exact match not found, and no similar cakes either',
          ranking: new Map(res.results.map(r => [r.id, r.score])),
          tags: res.tags,
        });
      } catch (err) {
        if (run !== imageSearchRun.current) return;
        console.error('Image search failed:', err);
        setImageSearch({ status: 'error', message: `Photo search failed: ${err.message}`, ranking: new Map(), tags: [] });
      }
    };
    window.addEventListener('pcake:image-search', onImageSearch);
    return () => window.removeEventListener('pcake:image-search', onImageSearch);
  }, [userRole, user]);

  const clearImageSearch = () => {
    imageSearchRun.current++;
    setImageSearch(null);
    window.dispatchEvent(new CustomEvent('pcake:image-search-cleared'));
  };

  // What the page shows: newest first; for Super narrowed by the word and photo searches.
  const visibleProfiles = useMemo(() => {
    let list = [...(customProfiles || [])].sort((a, b) => createdAtOf(b) - createdAtOf(a));
    if (userRole !== 'Super') return list;
    const q = (searchQuery || '').trim();
    if (q) list = list.filter(p => multiKeywordMatch(q, searchFieldsOf(p)));
    if (imageSearch?.ranking) {
      const rank = imageSearch.ranking;
      list = list.filter(p => rank.has(p.id)).sort((a, b) => rank.get(b.id) - rank.get(a.id));
    }
    return list;
  }, [customProfiles, userRole, searchQuery, imageSearch]);
  const searching = userRole === 'Super' && (!!(searchQuery || '').trim() || !!imageSearch);

  // Ticks only stay on cakes that are still on screen, so a delete never reaches hidden ones.
  useEffect(() => {
    setSelected(prev => {
      if (!prev.size) return prev;
      const shown = new Set(visibleProfiles.map(p => p.id));
      const kept = [...prev].filter(id => shown.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [visibleProfiles]);

  const openProfileCard = (profile = null) => {
    setSelectedProfile(profile ? { ...profile, isFromHome: !!profile.isFromHome } : null);
    setShowProfileCard(true);
  };

  const closeProfileCard = () => {
    setShowProfileCard(false);
    setSelectedProfile(null);
  };

  const createdOrUpdatedProfiles = visibleProfiles.filter(profile => !profile.isFromHome);
  const addedFromHomeOrFavouritesProfiles = visibleProfiles.filter(profile => profile.isFromHome);

  return (
    <SocialProvider user={user} enabled={userRole === 'Super'} onToast={showToast}>
    <div className="custom-page">
      {!showProfileCard ? (
        <ProcessGuidePanel steps={CUSTOM_BUYER_STEPS} userRole={userRole} column>
          <>
          {userRole === 'Super' && user && (
            <>
              <div className="social-custom-toolbar">
                {selectMode ? (
                  <>
                    <button type="button" className="social-btn" onClick={() => {
                      // "All" means all the cakes on screen — with a search, only the ones it found.
                      const all = visibleProfiles.map(p => p.id);
                      const allTicked = all.length > 0 && all.every(id => selected.has(id));
                      setSelected(allTicked ? new Set() : new Set(all));
                    }}>
                      <FaCheckSquare /> {visibleProfiles.length > 0 && visibleProfiles.every(p => selected.has(p.id)) ? 'None' : 'All'}
                    </button>
                    <label className="social-btn" title="Deleted because the import went wrong — the next import brings these in again" style={{ cursor: 'pointer' }}>
                      <input type="checkbox" checked={importIssue} onChange={(e) => setImportIssue(e.target.checked)} style={{ width: 14, height: 14, margin: 0 }} />
                      Import issue
                    </label>
                    <button type="button" className="social-btn social-btn--primary" onClick={deleteSelected} disabled={!selected.size || bulkBusy}>
                      <FaTrash /> {bulkBusy ? 'Deleting…' : `Delete (${selected.size})`}
                    </button>
                    <button type="button" className="social-btn social-btn--quiet" onClick={leaveSelect} aria-label="Stop selecting"><FaTimes /></button>
                  </>
                ) : (
                  <button type="button" className="social-btn" onClick={() => setSelectMode(true)} title="Select profiles to delete">
                    <FaCheckSquare /> Select
                  </button>
                )}
                <button type="button" className="social-btn" onClick={() => setShowImport(true)} title="Import cakes from Instagram, Facebook or WhatsApp">
                  <FaFileImport /> Import
                </button>
                <button type="button" className="social-btn social-btn--quiet" onClick={() => setShowAccounts(true)} title="Social accounts">
                  <FaUserCog />
                </button>
              </div>
              {justCreated && (
                <div className="social-created-banner">
                  <span>Profile “{justCreated.theme || justCreated.cflavour || 'new cake'}” created.</span>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <SocialShareButton profile={(customProfiles || []).find(p => p.id === justCreated.id) || justCreated} variant="wide" />
                    <button type="button" className="social-btn social-btn--quiet" onClick={() => setJustCreated(null)} aria-label="Dismiss"><FaTimes /></button>
                  </span>
                </div>
              )}
            </>
          )}
          {imageSearch && userRole === 'Super' && (
            <div className="custom-search-note" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
              margin: '6px 10px', padding: '6px 10px', borderRadius: '8px', fontSize: '13px',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                {imageSearch.status === 'working' && (
                  <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(250,72,72,.3)', borderTopColor: '#fa4848', animation: 'social-spin .8s linear infinite', flexShrink: 0 }} />
                )}
                <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
                  {imageSearch.message}
                  {imageSearch.status === 'done' && imageSearch.tags?.length > 0 && (
                    <span style={{ opacity: 0.7 }}> · {imageSearch.tags.slice(0, 5).join(', ')}</span>
                  )}
                </span>
              </span>
              <button onClick={clearImageSearch} title="Clear photo search" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '2px' }}>
                <FaTimes />
              </button>
            </div>
          )}
          {searching && !imageSearch && visibleProfiles.length === 0 && (customProfiles || []).length > 0 && (
            <p style={{ margin: '8px 12px', fontSize: '13px', opacity: 0.75 }}>No cakes in Custom match “{searchQuery.trim()}”.</p>
          )}
          {(userRole === 'Super' || userRole === 'Buyer') && (
            <div className="add-profile-section" style={{ marginTop: '0' }}>
              <div className="card-container">
                <div className="profile-card-wrapper">
                  <div
                    className="profile-card"
                    onClick={() => openProfileCard()}
                    style={{
                      minHeight: '150px',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <FaPlus style={{ color: '#666', fontSize: 'clamp(24px, 5vw, 36px)' }} />
                  </div>
                </div>
                {createdOrUpdatedProfiles.map(profile => (
                  <div key={profile.id} className={`profile-card-wrapper${selectMode && selected.has(profile.id) ? ' is-selected' : ''}`} style={{ position: 'relative' }}>
                    {selectMode && <SelectBox checked={selected.has(profile.id)} onToggle={() => toggleSelected(profile.id)} />}
                    <CustomProfileCard
                      theme={profile.theme}
                      subtitle={`Filling: ${profile.cfilling || profile.tiers?.[0]?.cfilling || profile.cfilling1 || 'None'}`}
                      flavour={profile.cflavour || profile.tiers?.[0]?.cflavour || 'Unknown'}
                      sponge={profile.sponge || profile.tiers?.[0]?.sponge || 'unknown'}
                      tiers={profile.tiers || []}
                      cover={profile.cover || 'none'}
                      eggless={profile.eggless || false}
                      glutenFree={profile.glutenFree || false}
                      quantity={profile.quantity || null}
                      ccost={profile.ccost || 'N/A'}
                      costEstimate={profile.costest || null}
                      image={profile.cimage || profile.images?.[0] || ''}
                      tierCount={profile.tierCount || 1}
                      multiTier={profile.multiTier || false}
                      moreTier={profile.moreTier || false}
                      cakeChargeData={cakeChargeData}
                      showPublishIcon={userRole === 'Super'}
                      showOrderIcon={userRole === 'Buyer'}
                      showDeleteIcon={true}
                      isFromHome={profile.isFromHome || false}
                      onPublish={() => handlePublish(profile)}
                      onOrder={() => handleAddToCart(profile)}
                      onDelete={() => handleDelete(profile.id)}
                      onEdit={() => openProfileCard(profile)}
                      userRole={userRole}
                      extraActions={userRole === 'Super' ? <SocialShareButton profile={profile} /> : null}
                      badge={profile.needsReview ? 'Review' : null}
                    />
                  </div>
                ))}
              </div>
              <hr style={{ border: '1px solid #ccc', margin: '5px 0 -10px 0' }} />
            </div>
          )}
          {!user ? (
            <p>Please log in to view your custom profiles.</p>
          ) : (
            <div className="card-container">
              {addedFromHomeOrFavouritesProfiles.map(profile => (
                <div key={profile.id} className={`profile-card-wrapper${selectMode && selected.has(profile.id) ? ' is-selected' : ''}`} style={{ position: 'relative' }}>
                  {selectMode && <SelectBox checked={selected.has(profile.id)} onToggle={() => toggleSelected(profile.id)} />}
                  <CustomProfileCard
                    theme={profile.theme}
                    subtitle={`Filling: ${profile.cfilling || profile.tiers?.[0]?.cfilling || profile.cfilling1 || 'None'}`}
                    flavour={profile.cflavour || profile.tiers?.[0]?.cflavour || 'Unknown'}
                    sponge={profile.sponge || profile.tiers?.[0]?.sponge || 'unknown'}
                    tiers={profile.tiers || []}
                    cover={profile.cover || 'none'}
                    eggless={profile.eggless || false}
                    glutenFree={profile.glutenFree || false}
                    quantity={profile.quantity || null}
                    ccost={profile.ccost || 'N/A'}
                    costEstimate={profile.costest || null}
                    image={profile.cimage || profile.images?.[0] || ''}
                    tierCount={profile.tierCount || 1}
                    multiTier={profile.multiTier || false}
                    moreTier={profile.moreTier || false}
                    cakeChargeData={cakeChargeData}
                    showPublishIcon={userRole === 'Super'}
                    showOrderIcon={userRole === 'Buyer'}
                    showDeleteIcon={true}
                    isFromHome={profile.isFromHome || false}
                    onPublish={() => handlePublish(profile)}
                    onOrder={() => handleAddToCart(profile)}
                    onDelete={() => handleDelete(profile.id)}
                    onEdit={() => openProfileCard(profile)}
                    userRole={userRole}
                    extraActions={userRole === 'Super' ? <SocialShareButton profile={profile} /> : null}
                  />
                </div>
              ))}
            </div>
          )}
          </>
        </ProcessGuidePanel>
      ) : (
        userRole === 'Super' ? (
          <CustomLargeCardSuper
            profile={selectedProfile}
            userRole={userRole}
            onClose={closeProfileCard}
            onSave={selectedProfile ? handleUpdateProfile : handleAddProfile}
            availableFlavours={availableFlavours}
            availableFillings={availableFillings}
            availableSponges={availableSponges}
            combinations={combinations}
            coverCombinations={coverCombinations}
            cEvents={cEvents}
            cShapes={cShapes}
            cakeChargeData={cakeChargeData}
            personServedData={personServedData}
            readOnly={false}
            isFromHome={selectedProfile?.isFromHome || false}
          />
        ) : (
          <CustomLargeCardUser
            profile={selectedProfile}
            userRole={userRole}
            onClose={closeProfileCard}
            onSave={selectedProfile ? handleUpdateProfile : handleAddProfile}
            availableFlavours={availableFlavours}
            availableFillings={availableFillings}
            availableSponges={availableSponges}
            combinations={combinations}
            coverCombinations={coverCombinations}
            cEvents={cEvents}
            cShapes={cShapes}
            cakeChargeData={cakeChargeData}
            personServedData={personServedData}
            readOnly={false}
            isFromHome={selectedProfile?.isFromHome || false}
          />
        )
      )}
      {showOngoingModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="ongoing-modal-card" style={{ borderRadius: '10px', width: '280px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ background: '#fa4848', color: '#fff', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '14px' }}>
              <span>On-going Enquiry not Finished</span>
            </div>
            <div style={{ padding: '14px', fontSize: '13px', color: '#444' }}>
              <p>You have an active enquiry in progress. Please wait for it to be completed before adding a new request.</p>
            </div>
            <div style={{ padding: '8px 14px 14px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowOngoingModal(false)} style={{ background: '#fa4848', color: '#fff', border: 'none', borderRadius: '5px', padding: '6px 16px', fontSize: '13px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
      {showAccounts && <ShareSheet mode="accounts" onClose={() => setShowAccounts(false)} />}
      {error && <p className="error" style={{ margin: '8px 10px' }}>{error}</p>}
    </div>
    </SocialProvider>
  );
};

export default Custom;
